import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { useMyPreferences } from '@/features/settings/hooks/usePreferences';
import { useAuth } from '@/auth/useAuth';
import { isOperatorExperience } from '@/auth/permissions';

// Same lazy-import shape as App.tsx so the page stays code-split; the module
// cache dedupes this with App-level imports of the same page.
const TodayPage = lazy(() =>
  import('@/features/today/TodayPage').then((m) => ({ default: m.TodayPage })),
);

/**
 * The authenticated "/" index. TODAY is the start page (2026-09-25, Michael:
 * "Make Today the starting page. Combine Today and My Action Center into one
 * place to see what needs attention. Move the management dashboard into
 * Reports."). The landing_page preference still applies: 'projects' opens the
 * projects list, and 'dashboard' — the old default — now opens the management
 * dashboard where it lives, under Reports. Unset, loading, failed, or 'today'
 * all render Today.
 */
export function LandingIndex() {
  const { user } = useAuth();
  const { data, isLoading } = useMyPreferences();

  // Operator experience (2026-09-17, spec §2.3): "/" is My job for an
  // operator — and since the portal login returns to the URL it left, this
  // is the login landing too. Before the preference: none of the pages a
  // landing_page could name is theirs.
  if (isOperatorExperience(user)) return <Navigate to="/my-job" replace />;

  // Don't paint the dashboard just to yank it away: wait out the (cached,
  // usually instant) pref fetch. Errors fall through to the default below.
  if (isLoading) return null;

  const target = data?.prefs.landing_page;
  if (target === 'dashboard') return <Navigate to="/reports/overview" replace />;
  if (target === 'projects') return <Navigate to="/projects" replace />;
  return <TodayPage />;
}
